import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_BASE_URL;

// Async Thunk for fetching ALL notifications
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('login');
      const response = await axios.get(`${apiUrl}/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      if (response.data?.status) {
        return response.data.data || [];
      }
      return rejectWithValue('Unexpected API response structure');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications');
    }
  }
);

// Async Thunk for marking all as read
export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('login');
      const response = await axios.get(`${apiUrl}/notifications/mark-all-as-read`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark notifications as read');
    }
  }
);

// Async Thunk for marking a single notification as read (Fire-and-forget logic)
export const markSingleAsRead = createAsyncThunk(
  'notifications/markOneRead',
  async (id) => {
    try {
      const token = localStorage.getItem('login');
      await axios.post(`${apiUrl}/notifications/mark-as-read/${id}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return id;
    } catch (error) {
      console.warn('Failed to mark notification as read on backend:', error);
      throw error;
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],
    unreadCount: 0,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    // Instantly mark as read in UI (Optimistic)
    dismissNotification(state, action) {
      const notificationId = action.payload;
      const notification = state.items.find(n => n.id === notificationId);
      if (notification && !notification.read_at) {
        notification.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    clearNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.status = 'idle';
      state.error = null;
    },
    addNotification: (state, action) => {
      const exists = state.items.some(n => n.id === action.payload.id);
      if (!exists) {
        state.items.unshift(action.payload);
        if (!action.payload.read_at) {
          state.unreadCount += 1;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const incoming = action.payload || [];
        const existingIds = new Set(state.items.map(i => i.id));
        const newItems = incoming.filter(i => !existingIds.has(i.id));
        const merged = [...state.items, ...newItems];
        state.items = merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        state.unreadCount = state.items.filter(n => !n.read_at).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(markAllNotificationsAsRead.pending, (state) => {
        state.status = 'marking';
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload.status) {
          state.unreadCount = 0;
          state.items = state.items.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }));
        }
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
    // markSingleAsRead is handled optimistically by dismissNotification reducer
  },
});

export const { dismissNotification, clearNotifications, addNotification } = notificationSlice.actions;
export default notificationSlice.reducer;
